import React, { useState, useMemo } from 'react';
import { useDataQuery } from '@dhis2/app-runtime';
import { Card, CircularLoader } from '@dhis2/ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useUserOrgUnits } from './useUserOrgUnits';
import { BarChart3 } from 'lucide-react'

const KPIPage = () => {
  const [timeRange, setTimeRange] = useState('current');
  const { userOrgUnits, loading: userLoading, error: userError } = useUserOrgUnits();

  // Get the first organization unit ID for the user
  const userOrgUnitIds = userOrgUnits.map(ou => ou.id);
  const primaryOrgUnitId = userOrgUnitIds[0];

  // FIXED: Use the user's organization units
  const { loading: kpiLoading, error: kpiError, data: kpiData } = useDataQuery({
    kpis: {
      resource: 'tracker/events',
      params: {
        orgUnit: primaryOrgUnitId,
        ouMode: 'DESCENDANTS',
        program: 'yiT00XKrrHP',
        programStage: 'ivyCLjNLFrd',
        pageSize: 1000,
        fields: 'event,eventDate,createdAt,orgUnitName,orgUnit,dataValues[dataElement,value]',
        order: 'createdAt:desc'
      }
    },
    objectives: {
      resource: 'tracker/events',
      params: {
        orgUnit: primaryOrgUnitId,
        ouMode: 'DESCENDANTS',
        program: 'yiT00XKrrHP',
        programStage: 'VIBtpvkpHFR',
        pageSize: 1000,
        fields: 'event,dataValues[dataElement,value]',
      }
    },
    tasks: {
      resource: 'tracker/events',
      params: {
        orgUnit: primaryOrgUnitId,
        ouMode: 'DESCENDANTS',
        program: 'yiT00XKrrHP',
        programStage: 'Nv9nYNcBTdM',
        pageSize: 1000,
        fields: 'event,dataValues[dataElement,value]',
      }
    }
  }, {
    lazy: !primaryOrgUnitId
  });

  // FIXED: Date formatting function
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Process KPI data with progress calculations
  const processedKPIs = useMemo(() => {
    if (!kpiData?.kpis?.instances) return [];

    return kpiData.kpis.instances.map(kpi => {
      const dataValues = kpi.dataValues || [];
      
      const name = dataValues.find(dv => dv.dataElement === 'DBmylDQA9KX')?.value || 'Unnamed KPI';
      const description = dataValues.find(dv => dv.dataElement === 'rQqF75V9t7K')?.value || '';
      const target = dataValues.find(dv => dv.dataElement === 'Ru68Ax2Xo2L')?.value || '0';
      const objectiveId = dataValues.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value;
      
      // Calculate progress from linked tasks
      let progress = 0;
      let currentValue = 0;
      
      if (objectiveId && kpiData.tasks?.instances) {
        const objectiveTasks = kpiData.tasks.instances.filter(task => 
          task.dataValues?.find(dv => dv.dataElement === 'plJovUlxfQ0')?.value === objectiveId
        );
        
        if (objectiveTasks.length > 0) {
          const totalCompletion = objectiveTasks.reduce((sum, task) => {
            const completion = parseInt(task.dataValues?.find(dv => dv.dataElement === 'AElpNxzTdRh')?.value) || 0;
            return sum + completion;
          }, 0);
          currentValue = Math.round(totalCompletion / objectiveTasks.length);
        }
      }

      const numericTarget = parseFloat(target) || 100;
      progress = numericTarget > 0 ? Math.round((currentValue / numericTarget) * 100) : currentValue;

      // Determine status
      let status = 'behind';
      let statusColor = '#dc2626';
      if (progress >= 90) {
        status = 'excellent';
        statusColor = '#059669';
      } else if (progress >= 70) {
        status = 'good';
        statusColor = '#f59e0b';
      } else if (progress >= 50) {
        status = 'fair';
        statusColor = '#f59e0b';
      }

      const lastUpdated = kpi.createdAt || kpi.eventDate;

      return {
        id: kpi.event,
        name,
        description,
        target: numericTarget,
        currentValue,
        progress,
        status,
        statusColor,
        objectiveId,
        lastUpdated: formatDate(lastUpdated),
        orgUnit: kpi.orgUnitName
      };
    });
  }, [kpiData]);

  // Prepare data for charts
  const chartData = useMemo(() => {
    return processedKPIs.map((kpi, index) => ({
      name: kpi.name.length > 12 ? kpi.name.substring(0, 12) + '...' : kpi.name,
      fullName: kpi.name,
      progress: kpi.progress,
      target: kpi.target,
      currentValue: kpi.currentValue,
      color: kpi.statusColor
    }));
  }, [processedKPIs]);

  // Calculate overall metrics
  const overallMetrics = useMemo(() => {
    const totalKPIs = processedKPIs.length;
    const averageProgress = totalKPIs > 0 
      ? Math.round(processedKPIs.reduce((sum, kpi) => sum + kpi.progress, 0) / totalKPIs)
      : 0;
    const onTrackKPIs = processedKPIs.filter(kpi => kpi.progress >= 70).length;

    return {
      totalKPIs,
      averageProgress,
      onTrackKPIs,
      onTrackPercentage: totalKPIs > 0 ? Math.round((onTrackKPIs / totalKPIs) * 100) : 0
    };
  }, [processedKPIs]);

  if (userLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <CircularLoader />
        <div style={{ color: '#6b7280', fontSize: '14px' }}>
          Loading user information...
        </div>
      </div>
    );
  }

  if (userError) {
    return (
      <Card style={{ padding: '24px', backgroundColor: '#fef2f2', margin: '24px' }}>
        <h3 style={{ color: '#dc2626', margin: '0 0 12px 0' }}>Error Loading User Data</h3>
        <div style={{ color: '#b91c1c' }}>
          {userError.message || 'Failed to load user information.'}
        </div>
      </Card>
    );
  }

  if (!primaryOrgUnitId) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px',
        padding: '24px'
      }}>
        <div style={{ fontSize: '48px' }}><BarChart3 /></div>
        <h3 style={{ margin: '0', color: '#6b7280' }}>No Organization Units</h3>
        <p style={{ margin: '0', color: '#9ca3af', textAlign: 'center' }}>
          User is not assigned to any organization units. Please contact administrator.
        </p>
      </div>
    );
  }

 
  // Loading state
  if (kpiLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <CircularLoader />
        <div style={{ color: '#6b7280', fontSize: '14px' }}>
          Loading KPI Dashboard...
        </div>
      </div>
    );
  }

  // Error state
  if (kpiError) {
    return (
      <Card style={{ padding: '24px', backgroundColor: '#fef2f2', margin: '24px' }}>
        <h3 style={{ color: '#dc2626', margin: '0 0 12px 0' }}>Error Loading KPI Data</h3>
        <div style={{ color: '#b91c1c' }}>
          {kpiError.message || 'Failed to load KPI data. Please try again.'}
        </div>
      </Card>
    );
  }

  // Progress Bar Component
  const ProgressBar = ({ value, color, height = '8px' }) => (
    <div style={{
      width: '100%',
      height,
      backgroundColor: '#e5e7eb',
      borderRadius: '4px',
      overflow: 'hidden'
    }}>
      <div style={{
        width: `${Math.min(Math.max(value, 0), 100)}%`,
        height: '100%',
        backgroundColor: color,
        borderRadius: '4px',
        transition: 'width 0.3s ease'
      }} />
    </div>
  );

  // Metric Card Component
  const MetricCard = ({ title, value, subtitle, color, icon }) => (
    <Card style={{
      padding: '24px',
      textAlign: 'center',
      background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
      border: `1px solid ${color}30`,
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      
    }}>
      <div style={{ 
        fontSize: '32px', 
        fontWeight: 'bold', 
        color: color,
        marginBottom: '8px'
      }}>
        {value}
      </div>
      <div style={{ 
        fontSize: '14px', 
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '4px'
      }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ 
          fontSize: '12px', 
          color: '#6b7280'
        }}>
          {subtitle}
        </div>
      )}
      {icon && (
        <div style={{ 
          fontSize: '24px',
          marginTop: '8px'
        }}>
          {icon}
        </div>
      )}
    </Card>
  );

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ 
        padding: '24px 24px 16px 24px',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <h2 style={{ 
              margin: '0 0 8px 0',
              fontSize: '24px',
              fontWeight: '700',
              color: '#111827'
            }}>
              Key Performance Indicators
            </h2>
            <p style={{ 
              margin: '0',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              Monitor and track performance metrics across your organization
            </p>
          </div>
          
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              backgroundColor: 'white'
            }}
          >
            <option value="current">Current Period</option>
            <option value="last_quarter">Last Quarter</option>
            <option value="last_year">Last Year</option>
          </select>
        </div>
      </div>

      {/* Overall Metrics */}
      <div style={{ padding: '24px', backgroundColor: '#f8fafc' }}>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          
          <MetricCard 
            title="Total KPIs" 
            value={overallMetrics.totalKPIs} 
            color="#4f46e5"
            icon="📊"
          />
          
          <MetricCard 
            title="Average Progress" 
            value={`${overallMetrics.averageProgress}%`} 
            color="#f59e0b"
            icon="📈"
          />
          <MetricCard 
            title="On Track" 
            value={`${overallMetrics.onTrackPercentage}%`} 
            subtitle={`${overallMetrics.onTrackKPIs} of ${overallMetrics.totalKPIs}`}
            color="#059669"
            icon="✅"
          />
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '24px' }}>
        {/* Chart Section */}
        {chartData.length > 0 && (
          <Card style={{ padding: '24px', marginBottom: '24px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1f2937' }}>
              KPI Progress Overview
            </h3>
            <div style={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    height={60}
                    fontSize={12}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    label={{ 
                      value: 'Progress (%)', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' }
                    }}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'progress') return [`${value}%`, 'Progress'];
                      return [value, name];
                    }}
                    labelFormatter={(value, payload) => {
                      if (payload && payload[0]) {
                        const data = payload[0].payload;
                        return (
                          <div>
                            <strong>{data.fullName}</strong>
                            <div>Target: {data.target}%</div>
                            <div>Current: {data.currentValue}%</div>
                            <div>Progress: {data.progress}%</div>
                          </div>
                        );
                      }
                      return value;
                    }}
                  />
                  <Bar dataKey="progress" name="Progress (%)" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        )}

        {/* KPI List */}
        <div>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#1f2937' }}>
            All Key Performance Indicators ({processedKPIs.length})
          </h3>

          {processedKPIs.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '16px'
            }}>
              {processedKPIs
                .sort((a, b) => b.progress - a.progress)
                .map(kpi => (
                  <Card key={kpi.id} style={{
                    padding: '20px',
                    borderRadius: '12px',
                    border: `1px solid ${kpi.statusColor}30`,
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '12px'
                      }}>
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#1f2937',
                          lineHeight: '1.3'
                        }}>
                          {kpi.name}
                        </h4>
                        <div style={{
                          padding: '4px 8px',
                          backgroundColor: `${kpi.statusColor}15`,
                          color: kpi.statusColor,
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {kpi.status.toUpperCase()}
                        </div>
                      </div>

                      {kpi.description && (
                        <p style={{ 
                          margin: '0 0 12px 0',
                          fontSize: '13px',
                          color: '#6b7280',
                          lineHeight: '1.4'
                        }}>
                          {kpi.description}
                        </p>
                      )}

                      <div style={{ marginBottom: '12px' }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '6px',
                          fontSize: '12px',
                          color: '#6b7280'
                        }}>
                          <span>Progress</span>
                          <span style={{ fontWeight: '600', color: kpi.statusColor }}>
                            {kpi.currentValue} / {kpi.target} ({kpi.progress}%)
                          </span>
                        </div>
                        <ProgressBar value={kpi.progress} color={kpi.statusColor} />
                      </div>
                    </div>

                    <div style={{ 
                      fontSize: '11px',
                      color: '#9ca3af',
                      borderTop: '1px solid #f3f4f6',
                      paddingTop: '12px',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{kpi.orgUnit}</span>
                      <span>Updated: {kpi.lastUpdated}</span>
                    </div>
                  </Card>
                ))}
            </div>
          ) : (
            <Card style={{ 
              padding: '40px', 
              textAlign: 'center',
              backgroundColor: '#f9fafb'
            }}>
              <div style={{ marginLeft: '20px', fontSize: '48px', marginBottom: '16px' }}><BarChart3 /></div>
              <h4 style={{ marginBottom: '16px',marginLeft: '20px', color: '#6b7280' }}>
                No KPIs Found
              </h4>
              <p style={{ marginLeft: '20px', color: '#9ca3af' }}>
                No Key Performance Indicators have been created yet.
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default KPIPage;